import React, { useEffect, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import {
  Box,
  Modal,
  Select,
  Button,
  Divider,
  MenuItem,
  TextField,
  Typography,
  ToggleButton,
  GlobalStyles,
  CircularProgress,
  ToggleButtonGroup,
} from "@mui/material";

import { esignIcons } from "../../../components/Esign/icons";

const labelStyle = {
  marginBottom: "8px",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "100%",
  color: "#1D1D1D",
};

const textFieldStyle = {
  mb: 1,
  "& .MuiOutlinedInput-root": {
    height: 46,
    borderRadius: "10px",
  },
};

const textFieldSx = {
  boxSizing: "border-box",
  borderRadius: "10px",
  backgroundColor: "#fff",
  borderColor: "#C0C0C0",
  borderWidth: "5px",
  mb: 1,
  "& .MuiOutlinedInput-root": {
    height: 46,
    borderRadius: "10px",
    borderColor: "#C0C0C0",
    backgroundColor: "none",
    "&:hover fieldset": {
      borderColor: "#C0C0C0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#C0C0C0",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "#FF6666",
  },
};

const FieldLabel = ({ children }) => (
  <Typography sx={labelStyle}>{children}</Typography>
);

const getInitialFormData = (template) => ({
  subject: template?.template_subject || "",
  templateName: template?.template_name || "",
  body: template?.body || "",
});

const TemplateFormModal = (props) => {
  const { open, mode, template, isFetching, isSubmitting, onClose, onSubmit } =
    props;

  const [formData, setFormData] = useState(getInitialFormData(template));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData(template));
      setErrors({});
    }
  }, [open, template]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.subject.trim()) {
      nextErrors.subject = "Email subject is required";
    }

    if (!formData.templateName.trim()) {
      nextErrors.templateName = "Template name is required";
    }

    if (!formData.body.trim()) {
      nextErrors.body = "Template body is required";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    let payload = {
      template_subject: formData.subject.trim(),
      template_name: formData.templateName.trim(),
      body: formData.body,
    };
    onSubmit(payload);
  };

  return (
    <Modal open={open} onClose={isSubmitting ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 974,
          height: 673,
          bgcolor: "#fff",
          boxShadow: 24,
          borderRadius: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: "16px 30px",
            height: 70,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}
          >
            {mode === "edit" ? "Edit Email Template" : "Add Email Template"}
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ height: 525, overflow: "auto", padding: "24px 32px" }}>
          <Box display="flex" gap={4}>
            {/* Left Section (fixed width) */}
            <Box sx={{ width: 370 }}>
              <Box>
                <FieldLabel>Email subject</FieldLabel>
                <TextField
                  fullWidth
                  placeholder="Enter subject"
                  value={formData.subject}
                  onChange={handleChange("subject")}
                  error={!!errors.subject}
                  helperText={errors.subject}
                  sx={textFieldSx}
                  InputProps={{
                    sx: {
                      borderRadius: "10px",
                      height: 46,
                      fontSize: "16px",
                      fontWeight: "500",
                    },
                  }}
                />
              </Box>
              <Box mt={1}>
                <FieldLabel>Template Name</FieldLabel>
                <TextField
                  fullWidth
                  placeholder="Enter template name"
                  value={formData.templateName}
                  onChange={handleChange("templateName")}
                  error={!!errors.templateName}
                  helperText={errors.templateName}
                  sx={textFieldSx}
                  InputProps={{
                    sx: {
                      borderRadius: "10px",
                      height: 46,
                      fontSize: "16px",
                      fontWeight: "500",
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  height: 285,
                  borderRadius: "12px",
                  mt: 2,
                  background:
                    "linear-gradient(90deg, rgba(34, 87, 173, 0.06) 0%, rgba(176, 17, 33, 0.06) 100%)",
                  border: "1px solid #dadada",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  gap: 2,
                  textAlign: "center",
                }}
              >
                <Box display={"flex"} alignItems={"center"} gap={2}>
                  {/* Logo */}
                  <Box
                    sx={{
                      width: 59,
                      height: 59,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #2A54A6 0%, #AC1325 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: 40,
                      animation: "spin 4s linear infinite", // infinite rotation
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "100%": {
                          transform: "rotate(360deg)",
                        },
                      },
                    }}
                  >
                    {esignIcons.meonAiAssistant}
                  </Box>

                  <Box mt={1}>
                    {/* Title */}
                    <Typography
                      fontWeight={600}
                      fontSize={"20px"}
                      color="#000000"
                      mb={0.5}
                      textAlign={"start"}
                    >
                      Meon AI Assistant
                    </Typography>

                    <Typography
                      fontWeight={400}
                      fontSize={"13px"}
                      color="#000000"
                      maxWidth={250}
                      lineHeight={"22px"}
                      textAlign={"start"}
                    >
                      Let AI build templates for you. Smart,
                      <br />
                      quick, and tailored to your needs.
                    </Typography>
                  </Box>
                </Box>
                {/* Input */}
                <TextField
                  disabled
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={3}
                  variant="outlined"
                  // placeholder="Write here..."
                  placeholder="Coming soon..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#fff",
                    },
                  }}
                />

                {/* Button */}
                <Button
                  disabled
                  variant="contained"
                  fullWidth
                  sx={{
                    width: 180,
                    height: 42,
                    borderRadius: "20px",
                    background:
                      "linear-gradient(135deg, #2A54A6 0%, #AC1325 100%)",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "16px",
                  }}
                >
                  ✦ Generate
                </Button>
              </Box>
            </Box>

            {/* Right Section (take remaining space) */}
            <Box flex={1}>
              <Box>
                <Box display="flex" flexDirection="column" gap={1}>
                  <FieldLabel>Email Template Editor</FieldLabel>
                  <GlobalStyles
                    styles={{
                      ".tox-promotion": { display: "none !important" },
                      ".tox-statusbar__branding": {
                        display: "none !important",
                      },
                      ".tox-tinymce": {
                        border: "1px solid #DFDFDF !important",
                      },
                    }}
                  />
                  <Editor
                    apiKey="ejfq37hgybsrwmucu0b2ec7eswr1e3r0t85x0tze0snanjkq"
                    value={formData.body || ""}
                    onEditorChange={(content) => {
                      setFormData((prev) => ({
                        ...prev,
                        body: content,
                      }));
                    }} 
                    init={{
                      height: 420,
                      menubar: false,
                      theme: "silver",
                      branding: false,
                      promotion: false,
                      plugins: [
                        "anchor",
                        "autolink",
                        "charmap",
                        "codesample",
                        "emoticons",
                        "image",
                        "link",
                        "lists",
                        "media",
                        "searchreplace",
                        "table",
                        "visualblocks",
                        "wordcount",
                        "code",
                      ],
                      toolbar:
                        "blocks fontfamily fontsize | bold italic underline strikethrough | link table code | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat",
                        toolbar_mode: 'wrap',
                      tinycomments_mode: "embedded",
                      tinycomments_author: "Author name",
                      valid_elements: "",
                      valid_styles: {
                        "*":
                          "color,font-size,font-weight,font-style,text-decoration,float," +
                          "margin,margin-top,margin-right,margin-bottom,margin-left," +
                          "padding,padding-top,padding-right,padding-bottom,padding-left," +
                          "background,background-color",
                      },
                      paste_data_images: true,
                      paste_retain_style_properties: "all",
                      fullpage_default_doctype: "<!DOCTYPE html>",
                      fullpage: true,
                      importcss_append: true,
                      content_css: false,
                      relative_urls: false,
                      remove_script_host: false,
                      convert_urls: false,
                      extended_valid_elements: "style[type|media]",
                      valid_children: "+head[style],+body[style]",
                      paste_webkit_styles: "all",
                      paste_data_images: true,
                      paste_preprocess: (plugin, args) => {
                        args.content = args.content
                          .replace(/<style[^>]*>/g, "<style>")
                          .replace(/<\/style>/g, "</style>");
                      },
                    }}
                  />
                </Box>
                <Typography
                  mt={1}
                  sx={{
                    fontWeight: 400,
                    fontSize: "13px",
                    lineHeight: "100%",
                    color: "#1D1D1D",
                  }}
                >
                  ({" "}
                  <Box component={"span"} fontWeight={600}>
                    Note:
                  </Box>{" "}
                  Variable should be as defined in workflow. Ex: [email_otp])
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderWidth: "1px" }} />
        <Box sx={{ p: "0 30px", mb: "15px", height: 70 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: "15px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                width: 130,
                height: 42,
                color: "#1D1D1D",
                background: "transparent",
                textTransform: "none",
                fontSize: "16px",
                borderRadius: "10px",
                border: "1px solid #D2D2D2",
                boxShadow: "none",
                fontWeight: "500",
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || isFetching}
              sx={{
                width: 150,
                height: 42,
                color: "#FFFFFF",
                background: "#185CB7",
                textTransform: "none",
                fontSize: "16px",
                borderRadius: "10px",
                boxShadow: "none",
                fontWeight: "500",
                "&:disabled": {
                  background: "#E4E4E4",
                  color: "#FFFFFF",
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : mode === "edit" ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default TemplateFormModal;
